<template>
  <div>
    <div class="md:py-56 py-48 bg-picker border-b">
      <div class="container">
        <div class="flex flex-col items-center">
          <h3 class="mb-24 text-heading md:text-heading-xl">
            {{ $lang('search', 'page_header') }}
          </h3>
          <div class="max-w-556 w-full">
            <TextInput
              class="mb-24"
              :placeholder="$lang('search', 'placeholder')"
              :has-error="status === LoadingStatuses.ERROR"
              has-search-icon
              :is-loading="status === LoadingStatuses.LOADING"
              :init-value="query"
              :is-white-bg="true"
              @submit-input="(e) => query = e"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="container xxl:px-24 md:px-56 px-0 md:py-56 py-48">
      <div
        v-if="query.length > 0 && results"
        class="search-results-number text-small-medium md:px-0 px-24"
      >
        <div>
          {{ results[0].nbHits + results[1].nbHits }}
          {{ $lang('search', 'hits') }}
        </div>
      </div>
      <div
        v-if="
          results &&
            results[0].nbHits + results[1].nbHits > 0 &&
            query.length > 0
        "
        class="mt-24"
      >
        <div>
          <div v-for="result in results" :key="result.id">
            <div v-if="result.index === productIndex">
              <div v-if="result.hits.length > 0" class="flex flex-wrap w-full">
                <ProductSpot
                  v-for="(product, i) in computedResults"
                  ref="productSpot"
                  :key="`${product.product || i}-main-productlist-spot`"
                  itemprop="itemListElement"
                  :product="
                    new Product({ ecomData: product, contentData: null })
                  "
                  :index="i"
                  :is-mostright="(!uiStore.isMobile && (i + 1) % 4 === 0) || (uiStore.isMobile && (i + 1) % 2 === 0 )"
                  :is-mobile="uiStore.isMobile"
                />
              </div>
              <button
                v-if="maxDisplay < results[0].hits.length"
                class="btn btn--primary btn--lg mx-auto mb-32 mt-40"
                @click="maxDisplay += 8"
              >
                {{ $lang('search', 'show_more_hits_button') }}
              </button>
            </div>

            <div v-if="result.index === contentIndex" class="md:px-0 px-24">
              <div v-if="result.hits.length > 0">
                <div class="mb-16 text-small-medium">
                  {{ $lang('search', 'other_hits_heading') }}
                </div>
                <div class="flex md:flex-row flex-col flex-wrap">
                  <div
                    v-for="content in result.hits"
                    :key="content.id"
                    class="product flex md:mb-0 mb-16 mr-16"
                  >
                    <nuxt-link
                      :to="content.permalink"
                      class="text-primary underline text-small-medium"
                    >
                      {{ content.title }}
                    </nuxt-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useConfigStore } from '~/store/config';
import { useUiStore } from '~/store/ui';
import useAlgoliaSearch from '~/composables/useAlgoliaSearch';
import TextInput from '~/components/forms/TextInput.vue';
import ProductSpot from '~/components/productlist/ProductSpot.vue';
import Product from '~/models/product';
import LoadingStatuses from '~/constants/loading-statuses';
const config = useConfigStore();
const uiStore = useUiStore();
const route = useRoute();
const { algoliaMultipleQueries } = useAlgoliaSearch();

const props = defineProps<{
  page: object,
}>();
/**
 * results: null as any,
 *       query: '',
 *       status: '',
 *       LoadingStatuses,
 *       Product,
 *       maxDisplay: 8,
 */

const status = ref('');
const query = ref<string>(route.query?.s as string || '');
const results = ref<any>(null);
const maxDisplay = ref(8);
const isSeller = useCookie<boolean>('isSeller', { default: () => false, maxAge: 3600 * 24 * 182, path: '/' });

const productIndex = computed(()=> {
  return config.algoliaBaseIndex + '_products';
});
const contentIndex = computed(()=> {
  return config.algoliaBaseIndex + '_content';
});

let productFilter = 'available:true AND NOT var_archived_value:"1"';
if (isSeller.value) {
  productFilter += ' AND brandName:"Ullmax"';
} 

const search = async() => {
  const queries = [
    {
      indexName: productIndex.value,
      query: query.value,
      params: {
        hitsPerPage: 1000,
        filters: productFilter, 
      },
    },
    {
      indexName: contentIndex.value,
      query: query.value,
    },
  ];

  const res = await algoliaMultipleQueries(queries);
  if (res) {
    results.value = res.results;
  } else {
    console.error();
  }
};

const computedResults = computed(() => {
  if (results.value) {
    return maxDisplay.value
      ? results.value[0].hits.slice(0, maxDisplay.value)
      : results.value[0].hits;
  }
  return false;
});

watch(query, () => {
  search();
});
onMounted(()=> {
  search();

});

</script>

<style scoped lang="postcss">
</style>
